/**
 * Company body-measurement definition catalog (admin/measurement-definition).
 *
 * NOTE: this is the company-scoped DEFINITIONS catalog. It is separate from
 * BodyMeasurementsService.js, which handles per-client measurement VALUES.
 *
 * Migrated to the OpenAPI-generated client. These functions keep their existing wrapper
 * return shape ({success, data, message} on the happy path, {success:false, message,...}
 * on error) so the UI callers are unaffected. The SDK's `data` == the old axios
 * `response.data`. Auth + X-Timezone come from configureApi's interceptor.
 *
 * Error messages go through `getApiErrorMessage`. They used to read `error.response?.data?.message`,
 * which the generated client never produces — it throws the parsed error BODY, and the server puts
 * its text under `errorMessage`. So every failure collapsed into the generic Hebrew fallback,
 * including the ones the server explains: a duplicate name, and the 409 refusing to hide or delete a
 * measurement that a computed measurement (נוסחת היקפים) derives its value from.
 *
 * IDOR-sweep note: the endpoints no longer accept `companyId` (query or body) — company is
 * derived from the authenticated principal. The generated types reject companyId, so we
 * DROP it from every call. Callers still pass a companyId arg; we simply don't forward it.
 *
 * Endpoints:
 *   GET    admin/measurement-definition                list catalog
 *   GET    admin/measurement-definition/{id}           one definition
 *   POST   admin/measurement-definition                create (or revive same name)
 *   POST   admin/measurement-definition/formula/{kind}  add the computed measurement (or revive it)
 *   GET    admin/measurement-definition/{id}/formula-preview  what the mapping yields company-wide
 *   PUT    admin/measurement-definition/{id}           update / rename
 *   GET    admin/measurement-definition/{id}/impact    value + trainee counts (delete dialog)
 *   DELETE admin/measurement-definition/{id}?force=    hide (force=false) or wipe (force=true)
 *   GET    admin/measurement-definition/hidden         list hidden (soft-deleted) definitions
 *   POST   admin/measurement-definition/{id}/restore   restore (un-hide) a hidden definition
 *   GET    admin/measurement-definition/grid?gender=   one gender's ordered grid
 *   PUT    admin/measurement-definition/{id}/genders   set which genders a def belongs to
 *   DELETE admin/measurement-definition/{id}/genders/{gender}  remove from ONE gender (keeps data)
 *   PUT    admin/measurement-definition/grid/reorder   bulk reorder within one gender
 */

import {
    adminBodyMeasurementDefinitionControllerGetCatalog,
    adminBodyMeasurementDefinitionControllerGetById,
    adminBodyMeasurementDefinitionControllerCreate,
    adminBodyMeasurementDefinitionControllerUpdate,
    adminBodyMeasurementDefinitionControllerGetDeleteImpact,
    adminBodyMeasurementDefinitionControllerDelete,
    adminBodyMeasurementDefinitionControllerGetHidden,
    adminBodyMeasurementDefinitionControllerRestore,
    adminBodyMeasurementDefinitionControllerGetGenderGrid,
    adminBodyMeasurementDefinitionControllerSetGenders,
    adminBodyMeasurementDefinitionControllerRemoveFromGender,
    adminBodyMeasurementDefinitionControllerReorderGenderGrid,
    adminBodyMeasurementDefinitionControllerCreateFormula,
    adminBodyMeasurementDefinitionControllerGetFormulaPreview,
} from '../api/generated';
import {getApiErrorMessage} from '../utils/apiError';

export const getMeasurementDefinitions = async (companyId) => {
    try {
        const {data, error} = await adminBodyMeasurementDefinitionControllerGetCatalog();
        if (error) throw error;
        return { success: true, data: data || [] };
    } catch (error) {
        console.error('Error fetching measurement definitions:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בטעינת מדדי הגוף"),
            data: [],
        };
    }
};

export const getMeasurementDefinitionById = async (id, companyId) => {
    try {
        const {data, error} = await adminBodyMeasurementDefinitionControllerGetById({
            path: {id},
        });
        if (error) throw error;
        return { success: true, data };
    } catch (error) {
        console.error('Error fetching measurement definition:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בטעינת המדד"),
            data: null,
        };
    }
};

export const createMeasurementDefinition = async (data) => {
    try {
        const {data: responseData, error} = await adminBodyMeasurementDefinitionControllerCreate({
            body: data,
        });
        if (error) throw error;
        return { success: true, data: responseData, message: "המדד נוצר בהצלחה" };
    } catch (error) {
        console.error('Error creating measurement definition:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה ביצירת המדד"),
        };
    }
};

/**
 * The company's computed measurement kinds. The value is the server's `MeasurementFormula` enum.
 *
 * A computed measurement is added through its own endpoint rather than the normal create form:
 * nothing about it is filled in by hand (the unit is always `%`, nobody types a value into it), and
 * the only thing to configure is which two measurements are its inputs — done afterwards, on its
 * edit page.
 */
/**
 * Typed off the generated path param rather than left to widen to `string`, so the day the server
 * adds a formula kind, the mismatch is a `npm run typecheck` failure here instead of a 400 at
 * runtime.
 *
 * @type {Record<string, import('../api/generated/types.gen').AdminBodyMeasurementDefinitionControllerCreateFormulaData['path']['formula']>}
 */
export const MEASUREMENT_FORMULAS = {
    CIRCUMFERENCE_BODY_FAT: 'CIRCUMFERENCE_BODY_FAT',
};

/** Hebrew label for a formula kind, for the add button and the catalog badge. */
export const formulaLabel = (formula) =>
    formula === MEASUREMENT_FORMULAS.CIRCUMFERENCE_BODY_FAT ? 'נוסחת היקפים' : 'מדד מחושב';

/**
 * Add the company's computed measurement, or revive the one they hid.
 *
 * Idempotent: a company that already has an active one gets it back rather than a duplicate. Every
 * company that existed before this feature already owns a (dormant, usually hidden) row, so in
 * practice this is most often a revive.
 *
 * @param {"CIRCUMFERENCE_BODY_FAT"} [formula] a `MEASUREMENT_FORMULAS` value. Typed as the literal
 *   union rather than `string` so adding a kind server-side is a compile error here, not a 400.
 */
export const createMeasurementFormula = async (formula = MEASUREMENT_FORMULAS.CIRCUMFERENCE_BODY_FAT) => {
    try {
        const {data, error} = await adminBodyMeasurementDefinitionControllerCreateFormula({
            path: {formula},
        });
        if (error) throw error;
        return {success: true, data, message: `${formulaLabel(formula)} נוסף בהצלחה`};
    } catch (error) {
        console.error('Error creating measurement formula:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בהוספת המדד המחושב"),
        };
    }
};

/**
 * What the computed measurement's current source mapping would actually produce across the company.
 *
 * A mapping can pass every validation and still be the wrong two measurements — map chest into the
 * neck slot and every trainee computes a negative percentage, which is dropped, which makes the
 * measurement silently vanish from every screen. This is what lets the edit page say so.
 */
export const getMeasurementFormulaPreview = async (id) => {
    try {
        const {data, error} = await adminBodyMeasurementDefinitionControllerGetFormulaPreview({
            path: {id},
        });
        if (error) throw error;
        return {success: true, data};
    } catch (error) {
        console.error('Error fetching formula preview:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בבדיקת המדידות שנבחרו"),
            data: null,
        };
    }
};

export const updateMeasurementDefinition = async (id, data) => {
    try {
        const {data: responseData, error} = await adminBodyMeasurementDefinitionControllerUpdate({
            path: {id},
            body: data,
        });
        if (error) throw error;
        return { success: true, data: responseData, message: "המדד עודכן בהצלחה" };
    } catch (error) {
        console.error('Error updating measurement definition:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בעדכון המדד"),
        };
    }
};

/** Value + trainee counts, for the delete dialog. */
export const getMeasurementDefinitionImpact = async (id, companyId) => {
    try {
        const {data, error} = await adminBodyMeasurementDefinitionControllerGetDeleteImpact({
            path: {id},
        });
        if (error) throw error;
        return { success: true, data };
    } catch (error) {
        console.error('Error fetching delete impact:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בבדיקת השפעת המחיקה"),
            data: { valueCount: 0, traineeCount: 0 },
        };
    }
};

/**
 * Delete a definition.
 *  force=false → hide (soft-delete); history preserved & revivable.
 *  force=true  → wipe all recorded values across all trainees, then hard-delete.
 */
export const deleteMeasurementDefinition = async (id, force = false) => {
    try {
        const {data, error} = await adminBodyMeasurementDefinitionControllerDelete({
            path: {id},
            query: {force},
        });
        if (error) throw error;
        return {
            success: true,
            data,
            message: force ? "המדד וכל הנתונים נמחקו" : "המדד הוסתר",
        };
    } catch (error) {
        console.error('Error deleting measurement definition:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה במחיקת המדד"),
        };
    }
};

/** The company's hidden (soft-deleted) definitions, for the restore UI. */
export const getHiddenMeasurementDefinitions = async (companyId) => {
    try {
        const {data, error} = await adminBodyMeasurementDefinitionControllerGetHidden();
        if (error) throw error;
        return { success: true, data: data || [] };
    } catch (error) {
        console.error('Error fetching hidden measurement definitions:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בטעינת המדדים המוסתרים"),
            data: [],
        };
    }
};

/** Restore (un-hide) a previously hidden definition. */
export const restoreMeasurementDefinition = async (id, companyId) => {
    try {
        const {data, error} = await adminBodyMeasurementDefinitionControllerRestore({
            path: {id},
        });
        if (error) throw error;
        return { success: true, data, message: "המדד שוחזר בהצלחה" };
    } catch (error) {
        console.error('Error restoring measurement definition:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בשחזור המדד"),
        };
    }
};

/**
 * One gender's ordered grid. Each item = { id, name, nameHeb, imageUrl, unit, computed,
 * legacyEnumType, displayOrder, genders: ["MALE","FEMALE"] }.
 * @param {"MALE"|"FEMALE"} gender
 */
export const getMeasurementGenderGrid = async (gender, companyId) => {
    try {
        const {data, error} = await adminBodyMeasurementDefinitionControllerGetGenderGrid({
            query: {gender},
        });
        if (error) throw error;
        return { success: true, data: data || [] };
    } catch (error) {
        console.error('Error fetching measurement gender grid:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בטעינת המדדים"),
            data: [],
        };
    }
};

/**
 * Set which genders a definition belongs to (male-only / female-only / both).
 * @param {Array<"MALE"|"FEMALE">} genders
 */
export const setMeasurementGenders = async (id, genders, companyId) => {
    try {
        const {error} = await adminBodyMeasurementDefinitionControllerSetGenders({
            path: {id},
            body: {genders},
        });
        if (error) throw error;
        return { success: true };
    } catch (error) {
        console.error('Error setting measurement genders:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בעדכון המגדר של המדד"),
        };
    }
};

/**
 * Remove a measurement from ONE gender's grid (keeps recorded data; reversible by re-adding).
 * Only valid when the measurement is in both genders — for a measurement's last gender, use the
 * hide/permanently-delete dialog instead.
 * @param {"MALE"|"FEMALE"} gender
 */
export const removeMeasurementFromGender = async (id, gender, companyId) => {
    try {
        const {error} = await adminBodyMeasurementDefinitionControllerRemoveFromGender({
            path: {id, gender},
        });
        if (error) throw error;
        return { success: true };
    } catch (error) {
        console.error('Error removing measurement from gender:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בהסרת המדד מהמגדר"),
        };
    }
};

/**
 * Bulk reorder within one gender's grid.
 * @param {"MALE"|"FEMALE"} gender
 * @param {Array<{definitionId:number, displayOrder:number}>} items
 */
export const reorderMeasurementGenderGrid = async (gender, items, companyId) => {
    try {
        const {error} = await adminBodyMeasurementDefinitionControllerReorderGenderGrid({
            body: {gender, items},
        });
        if (error) throw error;
        return { success: true };
    } catch (error) {
        console.error('Error reordering measurement gender grid:', error);
        return {
            success: false,
            message: getApiErrorMessage(error, "שגיאה בשמירת הסדר"),
        };
    }
};
